import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { formatDayYear, formatPeriod, formatRange } from '../../lib/format/dates.ts';
import { answerState } from '../../lib/requests/answer.ts';
import { requestLinesByWeek, type WeekRequestLine } from '../../lib/requests/week-requests.ts';
import { nextUpcoming } from '../../lib/scheduling/upcoming.ts';
import { ApiFailure, api, type RequestPeriod, type ScheduleDetail, type ScheduleSummary, type WeekView } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { ScheduleView } from '../components/ScheduleView.tsx';
import { WeekChangesNotice } from '../components/WeekChangesNotice.tsx';
import { WeekRequests } from '../components/WeekRequests.tsx';
import { copy } from '../copy/en-CA.ts';
import { openedFromAdmin } from '../nav.ts';
import { useSession } from '../session.tsx';
import { DraftToolbar } from './admin/DraftToolbar.tsx';

const c = copy.schedule;

/** Physicians are told here, on the page they open most, when requests are open. */
function RequestNotice() {
  const { user } = useSession();
  const [open, setOpen] = useState<{ period: RequestPeriod; mine: number; answeredOn: string | null } | null>(null);
  useEffect(() => {
    if (!user?.physicianId) return;
    api
      .requestPeriods()
      .then((r) => {
        const p = r.periods.find((x) => x.open);
        setOpen(p ? { period: p, mine: r.mine[p.id] ?? 0, answeredOn: r.answered[p.id] ?? null } : null);
      })
      .catch(() => {
        // The notice is a courtesy; the Requests page reports the real error.
      });
  }, [user?.physicianId]);
  if (!open) return null;
  // "Nothing needed this time" is an answer: never "You have not submitted any yet" after it.
  const answer = answerState(open.mine, open.answeredOn);
  const mine = answer === 'with-requests' ? c.requestNotice.some(open.mine) : answer === 'nothing-needed' ? copy.requests.nothingNeeded.home : c.requestNotice.none;
  // Not printed: a printed schedule is for the wall, not for this reminder.
  return (
    <div className="no-print">
      <Notice kind="info">
        {c.requestNotice.open(open.period.name, formatDayYear(open.period.dueDate))} {mine}{' '}
        <Link to={`/requests/${open.period.id}`}>{c.requestNotice.link}</Link>
      </Notice>
    </div>
  );
}

/**
 * On the home page, once the next schedule is saved: the page still shows
 * the current one, so say the next is out and link to it. Nobody looks under
 * History for weeks still to come. `shownId` is the schedule on screen,
 * never named as "the next" one.
 */
function UpcomingNotice({ shownId }: { shownId: string }) {
  const { user } = useSession();
  const [next, setNext] = useState<ScheduleSummary | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .schedules()
      .then((r) => {
        // The server's today (America/Toronto), not the browser's clock.
        if (!cancelled) setNext(nextUpcoming(r.schedules, r.today, shownId));
      })
      .catch(() => {
        // The notice is a courtesy; History lists the same schedules and reports the real error.
      });
    return () => {
      cancelled = true;
    };
  }, [shownId]);
  if (!next) return null;
  // Not printed, like the request notice.
  return (
    <div className="no-print">
      <Notice kind="info">
        {c.upcomingNotice.text(next.name, formatPeriod(next.startDate, next.endDate))}{' '}
        <Link to={`/schedules/${next.id}`}>{user?.physicianId ? c.upcomingNotice.mine : c.upcomingNotice.open}</Link>
      </Notice>
    </div>
  );
}

/**
 * After an admin saves a draft. The draft toolbar, where they pressed Save,
 * disappears with the draft, so the confirmation is said here, in its
 * place, and takes the keyboard focus the vanished button had.
 */
function SavedNotice() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div className="no-print saved-notice" ref={ref} tabIndex={-1}>
      <Notice kind="success">{copy.admin.draft.saved}</Notice>
    </div>
  );
}

/** Who asked for what, for the admin's Requests column: loaded, or could not be. */
type RequestsState = { scheduleId: string; lines: Map<string, WeekRequestLine[]>; names: Map<string, string> } | 'unavailable' | null;

/**
 * The schedule everyone reads: the current one at "/", or a specific one at
 * "/schedules/:id" (from History, or an admin's draft). Admins can edit any
 * week in place; a draft is always editable, a saved schedule once they say
 * so, because a change there is visible to everyone at once.
 */
export function SchedulePage() {
  const { user } = useSession();
  const { id } = useParams();
  const location = useLocation();
  const [detail, setDetail] = useState<ScheduleDetail | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [swapFrom, setSwapFrom] = useState<WeekView | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [requests, setRequests] = useState<RequestsState>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(undefined);
    setError(null);
    setEditing(false);
    setSwapFrom(null);
    setJustSaved(false);
    const load = id ? api.schedule(id).then((r) => r.schedule) : api.currentSchedule().then((r) => r.schedule);
    load
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // The Requests column (owner, 24 October 2026): for admins, on a draft, or on
  // a released schedule while they edit it. Only then is the admin-only
  // availability asked for, so nobody else's browser ever holds it.
  const requestsFor =
    user?.isAdmin === true && detail && (detail.schedule.status === 'draft' || editing)
      ? { id: detail.schedule.id, start: detail.schedule.startDate, end: detail.schedule.endDate }
      : null;
  const requestsKey = requestsFor ? `${requestsFor.id}|${requestsFor.start}|${requestsFor.end}` : null;
  useEffect(() => {
    setRequests(null);
    if (!requestsFor) return;
    let cancelled = false;
    api.admin.availability(requestsFor.start, requestsFor.end).then(
      (r) => {
        if (cancelled) return;
        // Names from the roster, and from the schedule for anyone no longer on it.
        const names = new Map([...(detail?.physicians ?? []), ...r.physicians].map((p) => [p.id, p.name]));
        const lines = requestLinesByWeek(
          r.cells.filter((cell) => names.has(cell.physicianId)),
          r.physicians.map((p) => p.id),
        );
        setRequests({ scheduleId: requestsFor.id, lines, names });
      },
      () => {
        // Said above the weeks, so a missing column never looks like nobody asked for anything.
        if (!cancelled) setRequests('unavailable');
      },
    );
    return () => {
      cancelled = true;
    };
    // requestsKey stands for requestsFor; the schedule's names only fill in who is no longer on the roster.
  }, [requestsKey]);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (detail === undefined) return <p className="muted">{c.loading}</p>;
  if (detail === null) {
    return (
      <>
        <div className="page-title">
          <h1>{c.title}</h1>
        </div>
        <RequestNotice />
        <section className="card">
          <p className="empty">{user?.isAdmin ? c.emptyAdmin : c.empty}</p>
        </section>
      </>
    );
  }

  const isAdmin = user?.isAdmin === true;
  const isDraft = detail.schedule.status === 'draft';
  const canEdit = isAdmin && (isDraft || editing);

  const reassign = async (week: WeekView, pid: string | null) => {
    setEditError(null);
    try {
      const r = await api.admin.reassign(detail.schedule.id, week.weekStart, pid);
      setDetail(r.schedule);
    } catch (err) {
      setEditError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    }
  };

  const swap = async (week: WeekView) => {
    if (!swapFrom) {
      setSwapFrom(week);
      return;
    }
    if (swapFrom.weekStart === week.weekStart) {
      setSwapFrom(null);
      return;
    }
    setEditError(null);
    try {
      const r = await api.admin.swap(detail.schedule.id, swapFrom.weekStart, week.weekStart);
      setDetail(r.schedule);
    } catch (err) {
      setEditError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    } finally {
      setSwapFrom(null);
    }
  };

  const renderControls = (week: WeekView): ReactNode => {
    const range = formatRange(week.onCallStart, week.onCallEnd);
    const choices = detail.physicians.filter((p) => p.active || p.id === week.physicianId);
    return (
      <div className="controls stack">
        <div className="row">
          <label className="visually-hidden" htmlFor={`phys-${week.weekStart}`}>
            {c.edit.physicianLabel(range)}
          </label>
          <select id={`phys-${week.weekStart}`} className="input small-select" value={week.physicianId ?? ''} onChange={(e) => void reassign(week, e.target.value || null)}>
            <option value="">{c.unassigned}</option>
            {choices.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={`btn small swap-btn ${swapFrom ? (swapFrom.weekStart === week.weekStart ? 'swap-from' : 'swap-target') : ''}`}
            aria-pressed={swapFrom?.weekStart === week.weekStart}
            aria-label={swapFrom ? (swapFrom.weekStart === week.weekStart ? c.edit.swapCancel : `${c.edit.swapWith}: ${range}`) : `${c.edit.swap}: ${range}`}
            onClick={() => void swap(week)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 16V4m0 0L3 8m4-4 4 4" />
              <path d="M17 8v12m0 0 4-4m-4 4-4-4" />
            </svg>
            {c.edit.swap}
          </button>
        </div>
      </div>
    );
  };

  const toolbar = (
    <>
      {/* First of the home page's notices: that an admin moved one of this physician's weeks is what they most need to know. */}
      {id ? null : <WeekChangesNotice />}
      <RequestNotice />
      {/* The home page only: a schedule opened by its address is already the one the person chose. */}
      {id ? null : <UpcomingNotice shownId={detail.schedule.id} />}
      {isDraft && isAdmin ? (
        <DraftToolbar
          detail={detail}
          onChange={setDetail}
          onSaved={(saved) => {
            setDetail(saved);
            setJustSaved(true);
          }}
        />
      ) : null}
      {justSaved && !isDraft ? <SavedNotice /> : null}
      {!isDraft && isAdmin ? (
        <div className="row no-print">
          <button type="button" className="btn" aria-pressed={editing} onClick={() => { setEditing((v) => !v); setSwapFrom(null); }}>
            {editing ? c.edit.stop : c.edit.start}
          </button>
          {editing ? <span className="muted small">{c.edit.savedNote}</span> : null}
        </div>
      ) : null}
      {canEdit ? (
        // Always present, so starting a swap never shifts the table below.
        <p className={`edit-hint no-print ${swapFrom ? 'active' : ''}`} aria-live="polite">
          {swapFrom ? c.edit.swapping(formatRange(swapFrom.onCallStart, swapFrom.onCallEnd)) : c.edit.hint}
        </p>
      ) : null}
      {editError ? <Notice kind="error">{editError}</Notice> : null}
      {requestsFor && requests === 'unavailable' ? (
        <div className="no-print">
          <Notice kind="info">{c.requests.unavailable}</Notice>
        </div>
      ) : null}
    </>
  );

  const shownRequests = requestsFor && requests !== null && requests !== 'unavailable' && requests.scheduleId === detail.schedule.id ? requests : null;
  const renderRequests = shownRequests
    ? (week: WeekView): ReactNode => <WeekRequests lines={shownRequests.lines.get(week.weekStart) ?? []} names={shownRequests.names} onCallId={week.physicianId} />
    : null;

  // Opened by its address (from History or Admin), the page says how to get back.
  const crumb = id ? (
    openedFromAdmin(location.state) ? (
      <Link to="/admin/schedules">{copy.admin.sections.schedules}</Link>
    ) : (
      <Link to="/history">{copy.history.title}</Link>
    )
  ) : undefined;

  return (
    <ScheduleView
      detail={detail}
      crumb={crumb}
      toolbar={toolbar}
      {...(canEdit ? { renderControls, controlsHeader: c.edit.controls } : {})}
      {...(renderRequests ? { renderRequests } : {})}
    />
  );
}
